import { Routes } from '@angular/router';
import { ActiveUserGuard } from '../guards/active-user.guard';
import { FeatureFlagGuard } from '../guards/feature-flag.guard';
import { HasRoleGuard } from '../guards/has-role.guard';
import { LoginRedirectGuard } from '../guards/login-redirect.guard';
import { VerticalGuard } from '../guards/vertical.guard';
import {
  ACCESS_MANAGEMENT_ROLES,
  API_KEY_ROLES,
  BRANCH_MANAGEMENT_GENERAL_ROLES,
  BRANCH_MANAGEMENT_SPECIFIC_ROLES,
  CASE_TRACKING_ROLES,
  CUSTOMER_PORTAL_ROLES,
  GROCERY_CASE_TRACKING_ROLES,
  INCIDENT_SUBMISSION_ROLES,
  LOCATION_GROUP_MANAGEMENT_ROLES,
  LOCATION_MANAGEMENT_GENERAL_ROLES,
  LOCATION_MANAGEMENT_SPECIFIC_ROLES,
  MARKET_SNAPSHOT_ROLES,
  REPORT_DASHBOARD_ROLES,
  REPORT_PORTAL_ROLES,
  TRANSACTIONS_ROLES,
  UNCERTAIN_ROLES,
  REVIEW_MANAGEMENT_ROLES,
} from '../helpers';
import { AdminUserManagementComponent } from '../public/admin-user-management/admin-user-management.component';
import { SecureHomeComponent } from '../secure/landing/secure-home.component';
import { LogoutComponent } from '../secure/logout/logout.component';
import { HomePageComponent } from '../secure/pages/home/home-page.component';
import { UserPreferencesComponent } from '../secure/pages/user-preferences/user-preferences.component';
import { ApikeyManagementComponent } from '../secure/tabs/apikey-management/apikey-management.component';
import { BlManagementComponent } from '../secure/tabs/bl-management/bl-management.component';
import { CaseTrackingPortalComponent } from '../secure/tabs/case-tracking-portal/case-tracking-portal.component';
import { CaseShareHistoryComponent } from '../secure/tabs/case-tracking-portal/share-history/case-share-history.component';
import { CustomerPortalComponent } from '../secure/tabs/customer-portal/customer-portal.component';
import { GroceryCasePortalComponent } from '../secure/tabs/grocery-case-portal/grocery-case-portal.component';
import { IncidentSubmissionComponent } from '../secure/tabs/incident-submission/incident-submission.component';
import { MarketSnapshotsComponent } from '../secure/tabs/market-snapshots/market-snapshots.component';
import { ReportDashboardComponent } from '../secure/tabs/report-dashboard/report-dashboard.component';
import { ReportPortalComponent } from '../secure/tabs/report-portal/report-portal.component';
import { TransactionsTabComponent } from '../secure/tabs/transactions/transactions-tab.component';
import { UncertainFacesComponent } from '../secure/tabs/uncertain/uncertain-faces.component';
import { ReviewManagementComponent } from '../secure/tabs/review-management/review-management.component';
import { ReviewActivityComponent } from '../secure/tabs/review-management/review-activity/review-activity.component';

export const securedRoutes: Routes = [
  {
    path: '',
    redirectTo: '/home',
    pathMatch: 'full',
  },

  /**
   * Adding new things here requires them to be tabs in tabs.ts.
   * Unless you add them to `routesToNotRerouteFor`, it will reroute to customer-portal.
   */
  {
    path: 'home',
    component: SecureHomeComponent,
    children: [
      { path: '', component: HomePageComponent, data: { allowedIfSuspended: true } },
      { path: 'user-preferences', component: UserPreferencesComponent, data: { allowedIfInactive: true } },
      {
        path: 'uncertain',
        component: UncertainFacesComponent,
        data: { requiredRoles: UNCERTAIN_ROLES, featureFlagName: 'enable_route_uncertain' },
      },
      { path: 'transactions', redirectTo: 'transactions/', pathMatch: 'full', data: { requiredRoles: TRANSACTIONS_ROLES } },
      { path: 'transactions/:locationId', component: TransactionsTabComponent, data: { requiredRoles: TRANSACTIONS_ROLES } },
      {
        path: 'customer-portal',
        component: CustomerPortalComponent,
        data: {
          requiredRoles: CUSTOMER_PORTAL_ROLES,
          featureFlagName: 'enable_route_customer_portal',
          allowedIfInactive: true,
          allowedIfInactiveRoles: ['admin', 'branch-owner', 'branch-manager'],
        },
      },
      {
        path: 'customer_portal',
        redirectTo: 'customer-portal',
        pathMatch: 'full',
      },
      { path: 'report-portal', component: ReportPortalComponent, data: { requiredRoles: REPORT_PORTAL_ROLES } },
      { path: 'report_portal', redirectTo: 'report-portal', pathMatch: 'full' },
      {
        path: 'case-tracking',
        component: CaseTrackingPortalComponent,
        data: { requiredRoles: CASE_TRACKING_ROLES, verticals: ['MicroMarket'] },
      },
      { path: 'case_tracking', redirectTo: 'case-tracking', pathMatch: 'full' },
      {
        path: 'case-tracking/:caseId/share-history',
        component: CaseShareHistoryComponent,
        data: { requiredRoles: CASE_TRACKING_ROLES, verticals: ['MicroMarket'] },
      },
      {
        path: 'grocery-case-portal',
        component: GroceryCasePortalComponent,
        data: { requiredRoles: GROCERY_CASE_TRACKING_ROLES, verticals: ['Grocery'] },
      },
      { path: 'usage-dashboard', redirectTo: 'review-management', pathMatch: 'full' },
      {
        path: 'review-management',
        component: ReviewManagementComponent,
        data: { requiredRoles: REVIEW_MANAGEMENT_ROLES, featureFlagName: 'enable_usage_dashboard' },
      },
      {
        path: 'review-management/locations/:locationId/activity',
        component: ReviewActivityComponent,
        data: { requiredRoles: REVIEW_MANAGEMENT_ROLES, featureFlagName: 'enable_usage_dashboard' },
      },
      {
        path: 'market-snapshots',
        component: MarketSnapshotsComponent,
        data: { requiredRoles: MARKET_SNAPSHOT_ROLES, featureFlagName: 'feature_flag_market_snapshots' },
      },
      {
        path: 'manage',
        redirectTo: 'manage/branch',
        pathMatch: 'full',
        data: { allowedIfSuspended: true, allowedIfSuspendedRoles: ['admin', 'branch-owner', 'branch-manager'] },
      },
      { path: 'manage/locationgroup', component: BlManagementComponent, data: { requiredRoles: LOCATION_GROUP_MANAGEMENT_ROLES } },
      {
        path: 'manage/locationgroup/:locationGroupId',
        component: BlManagementComponent,
        data: { requiredRoles: LOCATION_GROUP_MANAGEMENT_ROLES },
      },
      {
        path: 'manage/branch',
        component: BlManagementComponent,
        data: {
          requiredRoles: BRANCH_MANAGEMENT_GENERAL_ROLES,
          allowedIfSuspended: true,
          allowedIfSuspendedRoles: ['admin', 'branch-owner', 'branch-manager'],
        },
      },
      {
        path: 'manage/branch/:branchId',
        component: BlManagementComponent,
        data: {
          requiredRoles: BRANCH_MANAGEMENT_SPECIFIC_ROLES,
          allowedIfSuspended: true,
          allowedIfSuspendedRoles: ['admin', 'branch-owner', 'branch-manager'],
        },
      },
      {
        path: 'manage/branch/:branchId/location',
        component: BlManagementComponent,
        data: { requiredRoles: LOCATION_MANAGEMENT_GENERAL_ROLES },
      },
      {
        path: 'manage/branch/:branchId/location/:locationId',
        component: BlManagementComponent,
        data: { requiredRoles: LOCATION_MANAGEMENT_SPECIFIC_ROLES },
      },
      {
        path: 'manage/branch/:branchId/location/:locationId/camera/:locationCameraId',
        component: BlManagementComponent,
        data: { requiredRoles: LOCATION_MANAGEMENT_SPECIFIC_ROLES },
      },
      { path: 'incident-submission', component: IncidentSubmissionComponent, data: { requiredRoles: INCIDENT_SUBMISSION_ROLES } },
      { path: 'logout', component: LogoutComponent, data: { allowedIfInactive: true, allowedIfSuspended: true } },
      {
        path: 'user-management',
        component: AdminUserManagementComponent,
        data: { requiredRoles: ACCESS_MANAGEMENT_ROLES, allowedIfInactive: true },
      },
      {
        path: 'apikey',
        component: ApikeyManagementComponent,
        data: { requiredRoles: API_KEY_ROLES },
      },
      {
        path: 'reports',
        component: ReportDashboardComponent,
        data: { requiredRoles: REPORT_DASHBOARD_ROLES },
      },
    ],
    canActivate: [LoginRedirectGuard, ActiveUserGuard, FeatureFlagGuard],
    canActivateChild: [ActiveUserGuard, HasRoleGuard, FeatureFlagGuard, VerticalGuard],
    data: { allowedIfInactive: true, allowedIfSuspended: true },
  },
];
